import { Link as RouterLink } from "@tanstack/react-router";
import React, { type ReactNode, useMemo } from "react";
import cx from "ui/cx";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./Link.module.scss";

const EXTERNAL_PREFIXES = ["http:", "https:", "//", "mailto:", "tel:", "sms:", "blob:"];

// Makes href optional so we can use the same component for links with only onClick
export type LinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href?: string;
  disabled?: boolean;
  selected?: boolean;
  selectedCx?: string;
  disabledCx?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement> | null) => void;
  tooltip?: ReactNode;
};

export default function Link({
  href,
  children,
  className,
  disabled,
  selected,
  selectedCx,
  disabledCx,
  title,
  onClick,
  tooltip,
  ...props
}: LinkProps) {
  if (typeof disabled !== "boolean") {
    disabled = !href && !onClick;
  }
  const linkProps = {
    className: cx(
      styles.wrapper,
      disabled && styles.disabled,
      disabled && disabledCx,
      disabled && "disabled",
      selected && selectedCx,
      selected && styles.selected,
      className,
    ),
    title: title || (typeof children === "string" ? (children as string) : ""),
    children,
    onClick: disabled ? undefined : onClick,
    ...props,
  };

  const isExternal = href && EXTERNAL_PREFIXES.some((p) => href.startsWith(p));
  const isHash = href && href.startsWith("#");

  const parsedHref = useMemo(() => {
    if (!href || isExternal) {
      return undefined;
    }
    if (isHash) {
      return { hash: href.substring(1) };
    }
    const url = new URL(href, "http://placeholder");
    return {
      pathname: url.pathname,
      // Intentionally collapses duplicate keys (a=1&a=2 -> {a:"2"}). Repeated params are not supported here.
      search: url.search ? Object.fromEntries(url.searchParams) : undefined,
      hash: url.hash ? url.hash.substring(1) : undefined,
    };
  }, [href, isExternal, isHash]);

  let linkElement: React.ReactElement;
  if (href && !disabled) {
    if (isExternal) {
      linkElement = <a href={href} {...linkProps} />;
    } else {
      linkElement = (
        <RouterLink
          to={isHash ? "." : parsedHref?.pathname ?? href}
          search={isHash ? (prev: Record<string, unknown>) => prev : parsedHref?.search}
          hash={parsedHref?.hash}
          {...(linkProps as unknown as Record<string, unknown>)}
        />
      );
    }
  } else {
    linkElement = <a {...linkProps} />;
  }

  return wrapWithTooltip(linkElement, tooltip);
}
